/**
 * Encoding rules for Enhanced Response Types (ERT) data-collection
 * values. Every data-collection answer travels as a single STRING on
 * `subResponses[].responseValue` — including NUMERIC ("12.5", never
 * 12.5) and MULTI_SELECT (comma-joined) — so these helpers own the
 * parse / serialize / validate rules in one pure, testable place.
 *
 * Pure — no react, no side effects.
 */

/**
 * Character cap on TEXT responses, matching V1 desktop. Over-length
 * input is REFUSED rather than truncated: truncation silently discards
 * the tail of a paste, which reads to the auditor as data loss.
 */
export const TEXT_MAX_LENGTH = 100;

/**
 * Accepts empty, an optional leading minus, digits, and at most one
 * decimal point. Deliberately permissive about precision: V1 carries
 * no min / max / decimal-places configuration, so a stricter client
 * rule here would reject values the desktop app accepts for the very
 * same question.
 */
const NUMERIC_PATTERN = /^-?\d*\.?\d*$/;

/**
 * Separator for MULTI_SELECT values. Fixed by wire compatibility: V1
 * desktop, the audit summary table, and the CSV export all read the
 * field as comma-joined, so an option label containing a comma
 * corrupts the round-trip. That is a pre-existing V1 constraint
 * carried forward deliberately rather than diverging the two clients'
 * encodings.
 */
const MULTI_SEPARATOR = ',';

/** Reference-stable empty result, so callers can memoise on it. */
const NO_VALUES: readonly string[] = [];

/** True when the text is within the TEXT response cap. */
export function isWithinTextLimit(value: string): boolean {
  return value.length <= TEXT_MAX_LENGTH;
}

/**
 * True when the text is a legal partial numeric entry. Called per
 * keystroke, so it must accept in-progress input like `-` and `0.`
 * that isn't yet a complete number.
 */
export function isNumericEntry(value: string): boolean {
  return NUMERIC_PATTERN.test(value);
}

/** Splits a comma-joined MULTI_SELECT value into its selected options. */
export function parseMultiValue(value: string): readonly string[] {
  if (value === '') return NO_VALUES;
  return value.split(MULTI_SEPARATOR);
}

/**
 * Serializes a set of selected MULTI_SELECT options into the wire
 * string, ordered by the question's `options` list rather than by
 * selection order.
 *
 * The result is ordered by the question's `options` list, NOT by the
 * order the auditor picked. Two auditors who select the same options
 * in different orders must produce the identical stored string,
 * otherwise equal answers compare unequal and every reordered
 * re-render looks like a change worth saving. Unknown selected values
 * are dropped, since the result is rebuilt from `options`. An empty
 * result means "cleared", which the payload builder turns into an
 * omitted field.
 *
 * Used by the dropdown multi-select, which hands back the full selected
 * set on each change (Stencil owns the add/remove bookkeeping).
 */
export function serializeMultiValue(
  options: readonly string[],
  selectedValues: readonly string[],
): string {
  const chosen = new Set(selectedValues);
  return options.filter((candidate) => chosen.has(candidate)).join(MULTI_SEPARATOR);
}
